import { useCallback, useMemo } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import type { VehicleQuery, VehicleSort } from '../data/api'
import { EMPTY_FACETS, type Facets } from '../data/vehicles'
import { useFacetsQuery, useVehiclesQuery } from './useVehicleQueries'

/* The inventory browser's query, held in the URL rather than in state, and
   answered by the API rather than in the browser.

   Everything the filter panel can set is a search parameter, so a narrowed
   floor is something a visitor can send to someone, bookmark, or reach again
   with the back button — and the hero's search bar can hand this section a
   brand and a budget simply by navigating.

   Facets with more than one value are lists: ?brand=Toyota,Honda. The
   single-value form the hero already sends parses as a list of one, so
   nothing upstream had to change — and the same comma-separated form is what
   goes up to the API, which is the point: the URL the visitor can share and
   the request that answers it are the same query written once.

   This used to pull the whole floor down and narrow it here. It sends the
   query it means now: the rows come back filtered and sorted, and the counts
   beside the checkboxes come from /vehicles/facets rather than from a second
   copy of the filtering logic living in this file.

   Four filters, and only four — price, make, the models under it, and the
   paint. The API can still narrow on the model year, on what the price
   covers and on the monthly band, and the facets endpoint still counts them;
   the panel simply stopped asking, because those were the boxes visitors read
   past on the way to the ones they use. `payment` survives here without a
   control because the navigation's "On lease" chip is a link into this
   section rather than a box on the panel — it arrives in the URL, goes up
   with the request, and "Reset all" clears it like any other. */

export const SORTS: { value: VehicleSort; label: string }[] = [
  { value: 'featured', label: 'Featured first' },
  { value: 'newest', label: 'Newest in' },
  { value: 'price-asc', label: 'Price: low to high' },
  { value: 'price-desc', label: 'Price: high to low' },
]

export type Payment = '' | 'cash' | 'lease'

export type InventoryFilters = {
  payment: Payment
  minPrice: number | null
  maxPrice: number | null
  brands: string[]
  models: string[]
  colours: string[]
}

/** The list filters a checkbox can tick — every one of them a facet the API
    counts and narrows on. */
export type ListFilter = 'brands' | 'models' | 'colours'

/* The one place that knows what a filter is called — in the URL and, because
   they are deliberately the same word, in the request. */
const PARAM = {
  payment: 'payment',
  minPrice: 'minPrice',
  maxPrice: 'maxPrice',
  brands: 'brand',
  models: 'model',
  colours: 'colour',
} as const satisfies Record<keyof InventoryFilters, string>

const list = (value: string | null) =>
  value
    ? value
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean)
    : []

const number = (value: string | null) =>
  value !== null && value !== '' && !Number.isNaN(Number(value))
    ? Number(value)
    : null

export function useInventoryFilters() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { pathname, hash } = useLocation()

  /* The query is written by hand rather than through setSearchParams, which
     keeps only the search: it drops the fragment, and this section is reached
     by one (/home#cars). Losing it on the first box ticked would take the
     shareable link's anchor with it — and the router would read the change as
     a new page and throw the visitor back to the top. */
  const apply = useCallback(
    (next: URLSearchParams) =>
      /* replace, so a run of filter tweaks does not bury the page the visitor
         arrived on under a dozen history entries. */
      navigate(
        { pathname, search: next.toString(), hash },
        { replace: true },
      ),
    [navigate, pathname, hash],
  )

  const filters = useMemo<InventoryFilters>(
    () => ({
      payment: (params.get(PARAM.payment) as Payment) || '',
      minPrice: number(params.get(PARAM.minPrice)),
      maxPrice: number(params.get(PARAM.maxPrice)),
      brands: list(params.get(PARAM.brands)),
      models: list(params.get(PARAM.models)),
      colours: list(params.get(PARAM.colours)),
    }),
    [params],
  )

  const sort = (params.get('sort') as VehicleSort) ?? 'featured'

  /* The same filters, as the API takes them. Anything left undefined is not
     sent — axios drops it — so an untouched panel asks for the plain floor.

     `unsold` is not a filter the visitor sets: it is what the floor means on
     a storefront. A sold car keeps its own page, because a link somebody was
     sent should not 404, but it does not stand in the grid taking a slot
     nobody can buy. */
  const query = useMemo<VehicleQuery>(
    () => ({
      unsold: true,
      payment: filters.payment || undefined,
      minPrice: filters.minPrice ?? undefined,
      maxPrice: filters.maxPrice ?? undefined,
      brand: filters.brands.join(',') || undefined,
      model: filters.models.join(',') || undefined,
      colour: filters.colours.join(',') || undefined,
    }),
    [filters],
  )

  const rows = useVehiclesQuery(query, sort)
  /* The chosen makes narrow the model list, and nothing else on the panel:
     see the API's facets(). */
  const options = useFacetsQuery(filters.brands)

  const facets: Facets = options.data ?? EMPTY_FACETS

  const write = useCallback(
    (updates: Record<string, string | null>) => {
      const next = new URLSearchParams(params)

      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === '') next.delete(key)
        else next.set(key, value)
      }

      apply(next)
    },
    [params, apply],
  )

  const setFilter = useCallback(
    <K extends keyof InventoryFilters>(key: K, value: InventoryFilters[K]) => {
      const encoded = Array.isArray(value)
        ? value.join(',') || null
        : value === null
          ? null
          : String(value)

      /* Changing the makes invalidates whatever models were chosen under
         them — a Civic ticked under Honda is not a filter any more once
         Honda is unticked, and the API would not offer it back. */
      write(
        key === 'brands'
          ? { [PARAM.brands]: encoded, [PARAM.models]: null }
          : { [PARAM[key]]: encoded },
      )
    },
    [write],
  )

  /** Add or remove one entry of a list filter — what a checkbox does. */
  const toggle = useCallback(
    (key: ListFilter, entry: string) => {
      const current = filters[key]
      const next = current.includes(entry)
        ? current.filter((value) => value !== entry)
        : [...current, entry]

      setFilter(key, next)
    },
    [filters, setFilter],
  )

  const setPrice = useCallback(
    (range: { from: number; to: number }) =>
      write({
        /* A handle parked against its end of the track is not a filter, it is
           the default — and it leaves the URL clean. */
        [PARAM.minPrice]:
          range.from <= facets.minPrice ? null : String(range.from),
        [PARAM.maxPrice]: range.to >= facets.maxPrice ? null : String(range.to),
      }),
    [write, facets.minPrice, facets.maxPrice],
  )

  const setSort = useCallback(
    (value: VehicleSort) => write({ sort: value === 'featured' ? null : value }),
    [write],
  )

  /* Only the filters are cleared. Anything else riding the URL — the hero
     hands over the bank and the tenure a visitor picked — is not this
     panel's to throw away. */
  const clearAll = useCallback(() => {
    const next = new URLSearchParams(params)
    for (const param of Object.values(PARAM)) next.delete(param)
    apply(next)
  }, [params, apply])

  const activeCount = useMemo(
    () =>
      Object.values(filters).filter((value) =>
        Array.isArray(value) ? value.length > 0 : value !== '' && value !== null,
      ).length,
    [filters],
  )

  return {
    filters,
    facets,
    sort,
    /** The rows the API returned: filtered, sorted, and ready to render. */
    results: rows.data ?? [],
    /* Only the first load is a loading state. A filter change keeps the
       previous rows and the previous options on screen — see the queries —
       rather than collapsing the grid into skeletons under the cursor that
       ticked the box. */
    loading: rows.isPending || options.isPending,
    /** True while a changed query is in flight and last query's rows are still
        what is on screen. The grid dims rather than disappears. */
    refreshing: rows.isFetching && !rows.isPending,
    /* An unreachable API is not an empty floor, and the section says so
       rather than blaming the filters — see its error state. */
    error: rows.error ?? options.error,
    retry: rows.refetch,
    activeCount,
    setPrice,
    setSort,
    toggle,
    clearAll,
  }
}

export default useInventoryFilters
